import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Kpi, BucketBadge, CHANNELS, CHANNEL_META } from "@/components/ui-bits";
import { InsightsPanel } from "@/components/InsightsPanel";
import { OnboardingWelcome } from "@/components/OnboardingWelcome";
import { ImportHealthBadge } from "@/components/SmartImportAssistant";
import { DataHealthPanel } from "@/components/DataHealthPanel";
import { QuickStartChip } from "@/components/QuickStart";
import { InfoTip } from "@/components/InfoTip";
import { WatchDemoModal } from "@/components/WatchDemoModal";
import { exploreSampleAndGo } from "@/lib/sampleData";
import { fmtCr, fmtNum, fmtINR } from "@/lib/format";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, AreaChart, Area, CartesianGrid,
} from "recharts";
import {
  CurrencyInr, FolderSimpleUser, TrendUp, Handshake, CheckCircle, Target, Bank,
  Warning, PhoneCall, WhatsappLogo, UsersThree, Flag, ArrowsClockwise,
  ChatText, EnvelopeSimple, Phone, Calendar, CalendarCheck, CalendarX, SealCheck, Gavel, ArrowRight, TrendDown, Broom, PlusCircle, UploadSimple,
} from "@phosphor-icons/react";

const BUCKET_FILL = { Current: "#16A34A", "1-30": "#002FA7", "31-60": "#F59E0B", "61-90": "#EA580C", "90+": "#DC2626" };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [perf, setPerf] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [rollRisk, setRollRisk] = useState(null);
  const [hygiene, setHygiene] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  const seesLegal = ["super_admin", "client_admin", "manager", "auditor"].includes(user.role);
  const canReconcile = ["super_admin", "client_admin", "manager"].includes(user.role);

  const loadHygiene = () => api.get("/dashboard/data-hygiene").then((r) => setHygiene(r.data)).catch(() => {});

  const loadAll = () => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/charts").then((r) => setCharts(r.data)).catch(() => {});
    if (user.role !== "agent") api.get("/dashboard/agent-performance").then((r) => setPerf(r.data)).catch(() => {});
    if (seesLegal) api.get("/legal/upcoming-hearings", { params: { days: 30 } }).then((r) => setHearings(r.data)).catch(() => {});
    api.get("/dashboard/roll-risk").then((r) => setRollRisk(r.data)).catch(() => {});
    if (seesLegal) loadHygiene();
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user.role, seesLegal]);

  // Mark "View dashboard" for the Setup Progress chip (frontend-only, localStorage).
  useEffect(() => {
    const uid = user?.user_id || user?.email || "u";
    if (localStorage.getItem(`recoveros_dashboard_viewed_${uid}`) !== "1") {
      localStorage.setItem(`recoveros_dashboard_viewed_${uid}`, "1");
      window.dispatchEvent(new Event("recoveros:setup-changed"));
    }
  }, [user]);

  const refresh = () => {
    setRefreshing(true);
    Promise.allSettled([
      api.get("/dashboard/stats").then((r) => setStats(r.data)),
      api.get("/dashboard/charts").then((r) => setCharts(r.data)),
      user.role !== "agent" ? api.get("/dashboard/agent-performance").then((r) => setPerf(r.data)) : Promise.resolve(),
      api.get("/dashboard/roll-risk").then((r) => setRollRisk(r.data)),
      seesLegal ? loadHygiene() : Promise.resolve(),
    ]).then(() => toast.success("Dashboard refreshed")).finally(() => setRefreshing(false));
  };

  const reconcile = (accountIds) => {
    const body = accountIds ? { account_ids: accountIds } : {};
    api.post("/dashboard/data-hygiene/reconcile", body).then((r) => {
      toast.success(`Reconciled ${r.data.reconciled} account(s) • ₹${(r.data.total_cleared || 0).toLocaleString("en-IN")} cleared`);
      loadHygiene();
      api.get("/dashboard/stats").then((res) => setStats(res.data)).catch(() => {});
    }).catch((e) => toast.error(e.response?.data?.detail || "Reconcile failed"));
  };

  if (!stats) return <DashboardSkeleton />;

  const kpis = [
    { label: "Total Outstanding", value: fmtCr(stats.total_outstanding), icon: CurrencyInr, accent: "text-destructive", tip: "Sum of current outstanding across all open accounts." },
    { label: "Total Loan Amount", value: fmtCr(stats.total_loan), icon: Bank, accent: "text-primary", tip: "Total original loan/receivable value across the portfolio." },
    { label: "Legal Outstanding", value: fmtCr(stats.total_legal_outstanding), icon: Gavel, accent: "text-rose-600", tip: "Outstanding tied to accounts flagged as legal candidates." },
    { label: "Total Accounts", value: fmtNum(stats.total_accounts), icon: FolderSimpleUser },
    { label: "Recovery This Month", value: fmtCr(stats.recovery_this_month), icon: TrendUp, accent: "text-emerald-600", tip: "Payments recorded so far in the current calendar month." },
    { label: "Total PTP Value", value: fmtCr(stats.total_ptp_value), icon: Handshake, tip: "Total value of active Promises To Pay." },
    { label: "Settlements Approved", value: fmtNum(stats.settlements_approved), icon: CheckCircle, accent: "text-emerald-600" },
    { label: "Settled/Closed (Month)", value: fmtNum(stats.closures_this_month), icon: SealCheck, accent: "text-teal-600" },
    { label: "Collection Efficiency", value: `${stats.collection_efficiency}%`, icon: Target, tip: "Recovered amount as a percentage of the amount due in the period." },
    { label: "Accounts 90+ DPD", value: fmtNum(stats.accounts_above_90), icon: Warning, accent: "text-destructive", tip: "Accounts more than 90 days past due — highest delinquency risk. Canonical DPD is auto-calculated by RecoverOS from each account's Payment Due Date." },
    { label: "Calls Made Today", value: fmtNum(stats.calls_made_today), icon: PhoneCall },
    { label: "WhatsApp Sent", value: fmtNum(stats.whatsapp_sent), icon: WhatsappLogo, accent: "text-emerald-600" },
    { label: "Active Agents", value: fmtNum(stats.active_agents), icon: UsersThree },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle={user.role === "agent" ? "Your assigned portfolio at a glance" : "Executive collections overview"}
      >
        <button
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-card hover:bg-secondary px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
          data-testid="dashboard-refresh-btn"
        >
          <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </PageHeader>

      <QuickStartChip />
      <ImportHealthBadge />

      <OnboardingWelcome stats={stats} />

      {(stats.real_accounts ?? stats.total_accounts) === 0 && stats.total_accounts === 0 && ["client_admin", "manager", "super_admin"].includes(user.role) && (
        <div className="border border-[#0B3D91]/20 bg-[#0B3D91]/5 p-6 mb-6" data-testid="dashboard-import-cta">
          <div className="font-heading font-bold text-lg text-[#0B3D91]">Your RecoverOS workspace is ready</div>
          <p className="text-sm text-muted-foreground mt-0.5">Start with your own receivables — see where your money is stuck and what needs attention first.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => navigate("/import")} className="inline-flex items-center gap-1.5 rounded-sm bg-[#16A34A] hover:bg-[#138a3e] text-white px-4 py-2 text-sm font-semibold transition-colors" data-testid="dashboard-import-btn">
              <UploadSimple size={16} weight="bold" /> Upload Excel
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "dashboard_empty" } }))} className="inline-flex items-center gap-1.5 rounded-sm border-2 border-[#0B3D91] text-[#0B3D91] px-4 py-2 text-sm font-semibold hover:bg-[#0B3D91]/[0.05] transition-colors" data-testid="dashboard-add-one-btn">
              <PlusCircle size={16} weight="bold" /> Add one account
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-muted-foreground">Not ready with your data?</span>
            {["client_admin", "manager"].includes(user.role) && (
              <button onClick={() => exploreSampleAndGo(toast)} className="font-semibold text-[#0B3D91] hover:underline" data-testid="empty-sample-btn">
                Explore sample portfolio
              </button>
            )}
            <button onClick={() => setWatchOpen(true)} className="font-semibold text-muted-foreground hover:text-foreground" data-testid="empty-watch-demo-btn">
              Watch demo
            </button>
          </div>
          <WatchDemoModal open={watchOpen} onOpenChange={setWatchOpen} source="empty-dashboard"
            onSample={["client_admin", "manager"].includes(user.role) ? () => exploreSampleAndGo(toast) : undefined}
            onImport={() => navigate("/import")} />
        </div>
      )}

      {/* RecoveryOS Insights — money stuck at a glance */}
      <InsightsPanel compact />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="dashboard-kpis">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="border border-border bg-card p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-center justify-between">
                <span className="label-caps inline-flex items-center gap-1">{k.label}{k.tip ? <InfoTip text={k.tip} /> : null}</span>
                <Icon size={16} weight="bold" className="text-muted-foreground" />
              </div>
              <div className={`text-xl md:text-2xl font-black font-heading tracking-tight mt-2 mono ${k.accent || ""}`}>{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Portfolio Data Health / Import Quality Score */}
      <DataHealthPanel />

      {/* PTP strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <PtpStat label="PTP Due Today" value={stats.ptp_due_today} color="text-blue-600" icon={Flag} testId="ptp-due-today" />
        <PtpStat label="PTP Broken" value={stats.ptp_broken} color="text-destructive" icon={Warning} testId="ptp-broken" />
        <PtpStat label="PTP Fulfilled" value={stats.ptp_fulfilled} color="text-emerald-600" icon={CheckCircle} testId="ptp-fulfilled" />
      </div>

      {/* Today's manually logged activity */}
      {stats.activities_today && (
        <div className="border border-border bg-card p-5 mb-6" data-testid="activities-today-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-lg">Today's Logged Activity</h3>
              <p className="text-xs text-muted-foreground">Manually recorded interactions logged by your team today</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <MiniStat label="Calls" value={stats.activities_today.Call || 0} icon={PhoneCall} color="text-orange-600" testId="act-today-call" />
            <MiniStat label="WhatsApp" value={stats.activities_today.WhatsApp || 0} icon={WhatsappLogo} color="text-emerald-600" testId="act-today-whatsapp" />
            <MiniStat label="SMS" value={stats.activities_today.SMS || 0} icon={ChatText} color="text-blue-600" testId="act-today-sms" />
            <MiniStat label="Email" value={stats.activities_today.Email || 0} icon={EnvelopeSimple} color="text-violet-600" testId="act-today-email" />
            <MiniStat label="IVR" value={stats.activities_today.IVR || 0} icon={Phone} color="text-rose-600" testId="act-today-ivr" />
            <MiniStat label="Payments Received" value={stats.payments_received || 0} icon={CurrencyInr} color="text-emerald-600" testId="act-payments-received" />
          </div>
        </div>
      )}

      {/* Follow-up management */}
      {stats.followups_today !== undefined && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <PtpStat label="Today's Follow-ups" value={stats.followups_today} color="text-blue-600" icon={CalendarCheck} testId="followups-today" />
          <PtpStat label="Overdue Follow-ups" value={stats.followups_overdue} color="text-destructive" icon={CalendarX} testId="followups-overdue" />
          <PtpStat label="Upcoming Follow-ups" value={stats.followups_upcoming} color="text-emerald-600" icon={Calendar} testId="followups-upcoming" />
        </div>
      )}

      {/* Upcoming court hearings (next 30 days) */}
      {seesLegal && hearings.length > 0 && (
        <div className="border border-border bg-card p-5 mb-6" data-testid="upcoming-hearings-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Gavel size={20} weight="bold" className="text-rose-600" />
              <div>
                <h3 className="font-heading font-bold text-lg">Upcoming Court Hearings</h3>
                <p className="text-xs text-muted-foreground">Next 30 days — {hearings.length} scheduled</p>
              </div>
            </div>
            <button onClick={() => navigate("/legal")} className="text-sm text-primary font-medium inline-flex items-center gap-1 hover:underline" data-testid="hearings-view-all">
              All legal cases <ArrowRight size={14} weight="bold" />
            </button>
          </div>
          <div className="divide-y divide-border border border-border rounded-sm max-h-72 overflow-y-auto">
            {hearings.map((h) => {
              const urgent = h.days_until !== null && h.days_until <= 3;
              return (
                <button
                  key={h.account_id}
                  onClick={() => navigate(`/accounts/${h.account_id}`)}
                  className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-secondary/50"
                  data-testid={`hearing-${h.loan_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex flex-col items-center justify-center h-11 w-14 rounded-sm border shrink-0 ${urgent ? "bg-red-50 border-red-200 text-red-700" : "bg-secondary border-border text-foreground"}`}>
                      <span className="text-xs font-black font-heading mono leading-none">{h.days_until === 0 ? "Today" : `${h.days_until}d`}</span>
                      <span className="text-[9px] uppercase tracking-wide mt-0.5">{h.next_hearing_date?.slice(5)}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{h.borrower_name} <span className="mono text-xs text-muted-foreground">{h.loan_id}</span></div>
                      <div className="text-xs text-muted-foreground truncate">
                        {h.court_case_no ? `${h.court_case_no} • ` : ""}{h.advocate || "No advocate"} • {fmtINR(h.outstanding_amount)}
                      </div>
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-rose-100 text-rose-700 shrink-0">{h.legal_status || "Legal"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Roll-rate risk — early-bucket accounts about to slip */}
      {rollRisk && rollRisk.total_at_risk > 0 && (
        <div className="border border-amber-300 bg-amber-50/50 p-5 mb-6" data-testid="roll-risk-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendDown size={20} weight="bold" className="text-amber-600" />
              <div>
                <h3 className="font-heading font-bold text-lg">Roll-Rate Risk</h3>
                <p className="text-xs text-muted-foreground">{rollRisk.total_at_risk} accounts ({fmtINR(rollRisk.total_outstanding_at_risk)}) about to slip to a worse bucket within {rollRisk.threshold} days — act now to prevent roll-forward.</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {rollRisk.buckets.map((b) => (
              <div key={b.bucket} className="border border-border bg-card p-3" data-testid={`roll-bucket-${b.bucket}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold mono">{b.bucket}</span>
                  <ArrowRight size={12} weight="bold" className="text-amber-600" />
                  <span className="mono text-amber-700">{b.next_bucket}</span>
                </div>
                <div className="text-2xl font-black font-heading mono mt-1 text-amber-700">{b.at_risk_count}</div>
                <div className="text-[11px] text-muted-foreground">of {b.total_in_bucket} • {fmtINR(b.at_risk_outstanding)} at risk</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hygiene && hygiene.count > 0 && (
        <div className="border border-rose-300 bg-rose-50/50 p-5 mb-6" data-testid="data-hygiene-widget">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Broom size={20} weight="bold" className="text-rose-600" />
              <div>
                <h3 className="font-heading font-bold text-lg">Data Hygiene</h3>
                <p className="text-xs text-muted-foreground">
                  {hygiene.count} settled/closed account{hygiene.count > 1 ? "s" : ""} still carry a non-zero balance ({fmtINR(hygiene.total_stray)}). Reconcile to clear the stray outstanding.
                </p>
              </div>
            </div>
            {canReconcile && (
              <button
                onClick={() => reconcile(null)}
                className="shrink-0 inline-flex items-center justify-center gap-2 h-9 px-4 rounded-sm bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition-colors"
                data-testid="reconcile-all-btn"
              >
                <Broom size={15} weight="bold" /> Reconcile All
              </button>
            )}
          </div>
          <div className="border border-border bg-card divide-y divide-border max-h-64 overflow-y-auto" data-testid="data-hygiene-list">
            {hygiene.items.map((a) => (
              <div key={a.account_id} className="flex items-center gap-3 p-3 text-sm" data-testid={`hygiene-row-${a.loan_id}`}>
                <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm capitalize ${a.status === "settled" ? "bg-teal-100 text-teal-800" : "bg-slate-200 text-slate-700"}`}>{a.status}</span>
                <button onClick={() => navigate(`/accounts/${a.account_id}`)} className="min-w-0 flex-1 text-left hover:underline">
                  <span className="font-medium">{a.borrower_name}</span>
                  <span className="text-xs text-muted-foreground mono ml-2">{a.loan_id}</span>
                </button>
                <span className="mono font-semibold text-rose-700 shrink-0">{fmtINR(a.outstanding_amount)}</span>
                {canReconcile && (
                  <button
                    onClick={() => reconcile([a.account_id])}
                    className="shrink-0 text-xs font-semibold text-rose-600 hover:text-rose-800 underline"
                    data-testid={`hygiene-reconcile-${a.loan_id}`}
                  >
                    Reconcile
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts row 1 */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Recovery Trend" subtitle="Daily collections — last 14 days">
          {charts && (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={charts.recovery_trend}>
                <defs>
                  <linearGradient id="recGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16A34A" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Area type="monotone" dataKey="amount" stroke="#16A34A" strokeWidth={2} fill="url(#recGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="DPD Bucket Analysis" subtitle="Accounts by delinquency bucket">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip cursor={{ fill: "#f3f4f6" }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {stats.buckets.map((b) => <Cell key={b.bucket} fill={BUCKET_FILL[b.bucket]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <ChartCard title="Settlement Trend" subtitle="Settlement requests raised — last 14 days">
          {charts && (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={charts.settlement_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f3f4f6" }} />
                <Bar dataKey="count" fill="#7C3AED" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Recovery Closures" subtitle="Accounts settled or closed — last 6 months">
          {stats.closure_trend && (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.closure_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f3f4f6" }} formatter={(v) => [`${v} accounts`, "Closed"]} />
                <Bar dataKey="count" fill="#0D9488" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Communication Activity" subtitle="Messages & calls — last 14 days">
          {charts && (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={charts.comm_activity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f3f4f6" }} />
                <Bar dataKey="count" fill="#002FA7" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Communications Overview */}
      {stats.comm_channels && (
        <div className="border border-border bg-card p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-lg">Communications Overview</h3>
              <p className="text-xs text-muted-foreground">Outreach delivered to borrowers by channel</p>
            </div>
            <span className="label-caps">{stats.comm_channels.reduce((s, c) => s + c.count, 0)} total</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CHANNELS.map((ch) => {
              const m = CHANNEL_META[ch];
              const item = stats.comm_channels.find((c) => c.channel === ch) || { count: 0 };
              const Icon = m.Icon;
              return (
                <div key={ch} className="border border-border p-3 flex items-center gap-3" data-testid={`comm-channel-${ch}`}>
                  <div className="h-10 w-10 rounded-sm bg-secondary border border-border flex items-center justify-center shrink-0">
                    <Icon size={20} weight="bold" className={m.color} />
                  </div>
                  <div>
                    <div className="label-caps">{m.label}</div>
                    <div className="text-xl font-black font-heading mono leading-none mt-1">{item.count}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Agent performance */}
      {user.role !== "agent" && (
        <div className="border border-border bg-card">
          <div className="p-5 border-b border-border">
            <h3 className="font-heading font-bold text-lg">Agent Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-caps border-b border-border">
                  <th className="text-left p-3">Agent</th>
                  <th className="text-right p-3">Accounts</th>
                  <th className="text-right p-3">Calls</th>
                  <th className="text-right p-3">Connected</th>
                  <th className="text-right p-3">PTP</th>
                  <th className="text-right p-3">Settlements</th>
                  <th className="text-right p-3">Recovered</th>
                </tr>
              </thead>
              <tbody>
                {perf.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No agent data</td></tr>}
                {perf.map((p) => (
                  <tr key={p.agent} className="border-b border-border last:border-0 hover:bg-secondary/50">
                    <td className="p-3 font-medium">{p.agent}</td>
                    <td className="p-3 text-right mono">{fmtNum(p.accounts)}</td>
                    <td className="p-3 text-right mono">{fmtNum(p.calls)}</td>
                    <td className="p-3 text-right mono">{fmtNum(p.connected)}</td>
                    <td className="p-3 text-right mono">{fmtNum(p.ptp)}</td>
                    <td className="p-3 text-right mono">{fmtNum(p.settlements)}</td>
                    <td className="p-3 text-right mono font-semibold text-emerald-600">{fmtCr(p.recovered)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PtpStat({ label, value, color, icon: Icon, testId }) {
  return (
    <div className="border border-border bg-card p-4 flex items-center gap-3" data-testid={testId}>
      <div className="h-10 w-10 rounded-sm bg-secondary border border-border flex items-center justify-center shrink-0">
        <Icon size={20} weight="bold" className={color} />
      </div>
      <div>
        <div className="label-caps">{label}</div>
        <div className={`text-2xl font-black font-heading mono leading-none mt-1 ${color}`}>{value}</div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color, icon: Icon, testId }) {
  return (
    <div className="border border-border p-3 flex items-center gap-3" data-testid={testId}>
      <div className="h-9 w-9 rounded-sm bg-secondary border border-border flex items-center justify-center shrink-0">
        <Icon size={18} weight="bold" className={color} />
      </div>
      <div>
        <div className="label-caps">{label}</div>
        <div className="text-xl font-black font-heading mono leading-none mt-1">{value}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="border border-border bg-card p-5">
      <h3 className="font-heading font-bold text-lg">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4">{subtitle}</p>
      {children}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div data-testid="dashboard-skeleton" className="animate-pulse">
      <div className="h-8 w-56 bg-secondary rounded-sm mb-2" />
      <div className="h-4 w-72 bg-secondary/70 rounded-sm mb-6" />
      <div className="h-24 bg-secondary/50 border border-border rounded-sm mb-6" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="border border-border bg-card p-4">
            <div className="h-3 w-20 bg-secondary rounded-sm" />
            <div className="h-6 w-16 bg-secondary/80 rounded-sm mt-3" />
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="h-64 border border-border bg-card rounded-sm" />
        <div className="h-64 border border-border bg-card rounded-sm" />
      </div>
    </div>
  );
}

